import React, { useContext, useEffect, useRef, useState } from "react";
import { LanguageContext } from "../Context/LanguageContext";
import bg_1 from "../assets/BG/1.jpg";
import bg_2 from "../assets/BG/2.jpg";
import bg_3 from "../assets/BG/3.jpg";
import bg_4 from "../assets/BG/4.jpg";

import AOS from "aos";
import "aos/dist/aos.css";

const Hero = () => {
  const { texts, language } = useContext(LanguageContext);
  const imageRef = useRef(null);
  const currentIndexRef = useRef(0);
  const [isVisible, setIsVisible] = useState(true);

  const images = [bg_1, bg_2, bg_3, bg_4];

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: false,
      mirror: false,
    });

    const interval = setInterval(() => {
      nextImage();
    }, 5000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  const nextImage = () => {
    setIsVisible(false);
    setTimeout(() => {
      currentIndexRef.current = (currentIndexRef.current + 1) % images.length;
      if (imageRef.current) {
        imageRef.current.src = images[currentIndexRef.current];
      }
      setIsVisible(true);
    }, 300);
  };

  return (
    <div className="w-full h-screen relative overflow-hidden">
      {/* Background Image with Ken Burns Effect */}
      <div className="absolute inset-0 w-full h-full">
        <img
          className={`w-full h-full object-cover transition-all duration-700 ${
            isVisible ? "opacity-100 scale-105" : "opacity-0 scale-100"
          }`}
          ref={imageRef}
          src={images[0]}
          alt="Slideshow"
        />
      </div>

      {/* Modern gradient overlay - no red tint */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60 z-10"></div>

      {/* Animated geometric shapes - no red */}
      <div className="absolute inset-0 z-10 overflow-hidden">
        <div className="absolute top-1/4 -left-20 w-72 h-72 bg-white/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-white/5 rounded-full blur-3xl animate-pulse animation-delay-1000"></div>
      </div>

      {/* Content overlay with enhanced glassmorphism */}
      <div className="absolute inset-0 z-20 flex flex-col justify-center items-center px-3 sm:px-4 md:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto text-center w-full">
          {/* Premium glassmorphism card with border glow */}
          <div className="relative group">
            {/* Main card */}
            <div className="relative bg-gradient-to-br from-black/50 to-black/40 rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 lg:p-10 border border-white/30 shadow-2xl">
              {/* Decorative corner accents */}
              <div className="absolute top-3 left-3 w-6 h-6 border-t-2 border-l-2 border-white rounded-tl-lg"></div>
              <div className="absolute top-3 right-3 w-6 h-6 border-t-2 border-r-2 border-white rounded-tr-lg"></div>
              <div className="absolute bottom-3 left-3 w-6 h-6 border-b-2 border-l-2 border-white rounded-bl-lg"></div>
              <div className="absolute bottom-3 right-3 w-6 h-6 border-b-2 border-r-2 border-white rounded-br-lg"></div>

              {/* Badge/Label */}
              <div
                className="inline-flex items-center gap-2 bg-primary-400/20 backdrop-blur-sm px-3 py-1.5 rounded-full border border-primary-400/30 mb-4"
                data-aos="fade-down"
                data-aos-duration="600"
              >
                <span className="w-1.5 h-1.5 bg-primary-400 rounded-full animate-pulse"></span>
                <span className="text-white/90 text-xs font-semibold tracking-wide">
                  {texts.since_2009}
                </span>
              </div>

              <h1
                className="text-white font-extrabold text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl mb-3 sm:mb-4 md:mb-5 py-4 sm:py-5 md:py-6 leading-relaxed"
                data-aos="zoom-in"
                data-aos-offset="200"
                data-aos-easing="ease-out-cubic"
                data-aos-duration="800"
              >
                <span
                  className="inline-block bg-gradient-to-r from-white via-white to-primary-100 bg-clip-text text-transparent drop-shadow-lg px-2 py-1"
                  style={{ lineHeight: "1.4" }}
                >
                  {texts.hero_title.split("|").map((line, index, array) => (
                    <React.Fragment key={index}>
                      <span className="inline-block">{line}</span>
                      {index < array.length - 1 && (
                        <br className="block my-1" />
                      )}
                    </React.Fragment>
                  ))}
                </span>
              </h1>

              <p
                className="text-white/95 font-medium text-xs sm:text-sm md:text-base lg:text-lg mb-4 sm:mb-6 md:mb-7 max-w-2xl mx-auto leading-relaxed"
                data-aos="fade-up"
                data-aos-delay="200"
                data-aos-duration="800"
              >
                {texts.hero_subtitle}
              </p>

              {/* CTA Buttons */}
              <div
                className={`flex flex-col sm:flex-row justify-center gap-2 sm:gap-3 ${
                  language === "ar"
                    ? "flex-row-reverse sm:flex-row-reverse"
                    : ""
                }`}
                data-aos="fade-up"
                data-aos-delay="400"
                data-aos-duration="800"
              >
                <a
                  href="/about"
                  className="group relative overflow-hidden bg-gradient-to-r from-primary-400 to-primary-500 hover:from-primary-500 hover:to-primary-600 text-white font-bold px-5 sm:px-7 py-2 sm:py-3 rounded-full transition-all duration-500 transform hover:scale-105 hover:shadow-xl flex items-center justify-center gap-2 text-xs sm:text-sm"
                >
                  {language === "ar" ? (
                    <>
                      <span className="relative z-10">{texts.about_us}</span>
                      <svg
                        className="w-3.5 h-3.5 sm:w-4 sm:h-4 transition-transform duration-300 relative z-10 group-hover:-translate-x-1 rotate-180"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 7l5 5m0 0l-5 5m5-5H6"
                        />
                      </svg>
                    </>
                  ) : (
                    <>
                      <span className="relative z-10">{texts.about_us}</span>
                      <svg
                        className="w-3.5 h-3.5 sm:w-4 sm:h-4 transition-transform duration-300 relative z-10 group-hover:translate-x-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 7l5 5m0 0l-5 5m5-5H6"
                        />
                      </svg>
                    </>
                  )}
                  {/* Shine effect */}
                  <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
                </a>

                <a
                  href="#contact"
                  className="group bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white font-bold px-5 sm:px-7 py-2 sm:py-3 rounded-full transition-all duration-300 transform hover:scale-105 border-2 border-white/30 hover:border-white/50 flex items-center justify-center gap-2 text-xs sm:text-sm"
                >
                  {language === "ar" ? (
                    <>
                      <span>{texts.contact}</span>
                      <svg
                        className="w-3.5 h-3.5 sm:w-4 sm:h-4 group-hover:rotate-12 transition-transform duration-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                    </>
                  ) : (
                    <>
                      <span>{texts.contact}</span>
                      <svg
                        className="w-3.5 h-3.5 sm:w-4 sm:h-4 group-hover:rotate-12 transition-transform duration-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                    </>
                  )}
                </a>
              </div>

              {/* Stats or features - optional */}
              <div
                className="grid grid-cols-3 gap-2 sm:gap-4 mt-5 sm:mt-7 pt-5 sm:pt-7 border-t border-white/20"
                data-aos="fade-up"
                data-aos-delay="600"
              >
                <div className="text-center">
                  <div className="text-white font-bold text-lg sm:text-xl md:text-2xl mb-0.5">
                    15+
                  </div>
                  <div className="text-white/70 text-xs font-medium">
                    {texts.years}
                  </div>
                </div>
                <div className="text-center border-x border-white/20">
                  <div className="text-white font-bold text-lg sm:text-xl md:text-2xl mb-0.5">
                    100K+
                  </div>
                  <div className="text-white/70 text-xs font-medium">
                    {texts.customers}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-white font-bold text-lg sm:text-xl md:text-2xl mb-0.5">
                    10+
                  </div>
                  <div className="text-white/70 text-xs font-medium">
                    {texts.locations}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced floating decorative elements */}
      <div className="absolute top-20 left-10 w-4 h-4 bg-primary-400/40 rounded-full animate-float hidden lg:block shadow-lg shadow-primary-400/50"></div>
      <div className="absolute bottom-32 right-16 w-6 h-6 bg-primary-300/50 rounded-full animate-float animation-delay-1000 hidden lg:block shadow-lg shadow-primary-300/50"></div>
      <div className="absolute top-1/3 right-8 w-3 h-3 bg-primary-500/60 rounded-full animate-float animation-delay-2000 hidden lg:block shadow-lg shadow-primary-500/50"></div>
      <div className="absolute bottom-1/4 left-1/4 w-5 h-5 bg-white/30 rounded-full animate-float animation-delay-500 hidden xl:block"></div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 animate-bounce hidden md:block">
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
          <div className="w-1 h-2 bg-white/70 rounded-full animate-pulse"></div>
        </div>
      </div>
    </div>
  );
};

export default Hero;
